'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import type { z } from 'zod';

import SectionHead from '@/components/SectionHead';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/use-toast';
import { useAuthStore } from '@/presentation/hooks/useStores';
import { formSchema } from '@/validations/GuestbookValidation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '@/components/ui/select';
import { parsePhoneNumber, REGIONS_IN_GHANA } from '@/libs/utils';
import { CountryCode } from 'libphonenumber-js';

export const Account = observer(
  ({
    index: _index,
    totalProcess: _totalProcess,
    onIndexChange,
  }: {
    index: number;
    totalProcess: number;
    onIndexChange: (index: number) => void;
  }) => {
    const authStore = useAuthStore();
    const partnerRequiresOtp = authStore.partnerAuthSession?.requiresOtp ?? false;
    const hasPartnerSession = authStore.hasPartnerSession;
    const isPartnerPrevalidated = hasPartnerSession && !partnerRequiresOtp;
    const parsedNumber = parsePhoneNumber(
      authStore.partnerAuthSession?.userData.msisdn || ''
    );
    // Load existing registration data when component mounts
    useEffect(() => {
      authStore.loadRegistrationData();

      // Check for partner token in localStorage
      if (typeof window !== 'undefined') {
        const partnerToken = localStorage.getItem('partner_token');
        if (partnerToken && !authStore.hasPartnerSession) {
          // Re-validate the partner token to ensure it's still valid
          authStore.validatePartnerSession(partnerToken).catch(() => {
            // If token is invalid, remove it
            localStorage.removeItem('partner_token');
          });
        }
      }
    }, [authStore]);

    const handleNext = () => {
      // Move to next step in registration flow
      onIndexChange(2);
    };

    const form = useForm<z.infer<typeof formSchema>>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        phone: hasPartnerSession
          ? parsedNumber.cleaned
          : authStore.registrationData?.phone || '',
        email: authStore.registrationData?.email || undefined,
        region: authStore.registrationData?.region || '',
        marketing_consent: true,
        whatsapp_same_number: false,
      },
    });

    async function onSubmit(values: z.infer<typeof formSchema>) {
      try {
        // Store form data in AuthStore for later use in registration
        authStore.updateRegistrationData({
          email: values.email,
          phone: values.phone,
          region: values.region,
          whatsappSameNumber: values.whatsapp_same_number,
          acceptsMarketing: values.marketing_consent,
        });

        // Store user data in session storage for the OTP process (backward compatibility)
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('number', values.phone);
        }

        // Step 1: Phone Lookup (according to docs/auth.md)
        try {
          const phoneCheck = await authStore.lookupPhone(values.phone);
          if (phoneCheck.exists) {
            toast({
              title: 'Phone number already registered',
              description:
                'This phone number is already in use. Please use a different number or try logging in.',
              variant: 'destructive',
            });
            return;
          }
        } catch (error: any) {
          // If phone lookup fails, we can still continue
          void error;
        }

        if (!isPartnerPrevalidated) {
          // Step 2: Request OTP (according to docs/auth.md)
          await authStore.requestOTPNew(values.phone, 'SIGNUP');
        }

        // Move to the next step (OTP verification)
        handleNext();
      } catch (error: any) {
        toast({
          title: 'Registration Failed',
          description:
            error.message ||
            'Failed to send verification code. Please try again.',
          variant: 'destructive',
        });
      }
    }

    return (
      <>
        <SectionHead
          title="Create your account"
          description=" Complete the fields below to get started with creating your account."
        />
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="w-full space-y-5 md:space-y-6"
          >
            <div className={'space-y-2'}>
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Phone Number</FormLabel>
                    <FormControl>
                      <PhoneInput
                        countries={
                          (hasPartnerSession
                            ? [parsedNumber.countryCode]
                            : ['GH']) as CountryCode[]
                        }
                        defaultCountry="GH"
                        placeholder="055"
                        disabled={hasPartnerSession}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                render={({ field }) => {
                  return (
                    <FormItem>
                      <span className="flex items-center justify-between">
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={field.disabled}
                        />
                        <p className="w-10/12 text-sm text-slate-400">
                          Is this number of your active whatApp number?
                        </p>
                      </span>
                    </FormItem>
                  );
                }}
                name={'whatsapp_same_number'}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email address</FormLabel>
                  <FormControl>
                    <Input placeholder="example.com" {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="region"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Region of residence</FormLabel>
                  <FormControl>
                    <Select
                      {...field}
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select your region" />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS_IN_GHANA.map((region) => (
                          <SelectItem
                            key={region}
                            value={region}
                            className="cursor-pointer hover:bg-motitoBlue/10"
                          >
                            {region}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  {/* <FormDescription>
                        This is your public display name.
                      </FormDescription> */}
                  <FormMessage />
                </FormItem>
              )}
            />
            {/*<FormField*/}
            {/*  control={form.control}*/}
            {/*  name="password"*/}
            {/*  render={({ field }) => (*/}
            {/*    <FormItem>*/}
            {/*      <FormLabel>Password</FormLabel>*/}
            {/*      <FormControl>*/}
            {/*        <Input*/}
            {/*          placeholder="Enter password here"*/}
            {/*          {...field}*/}
            {/*          type="password"*/}
            {/*        />*/}
            {/*      </FormControl>*/}
            {/*      /!* <FormDescription>*/}
            {/*    This is your public display name.*/}
            {/*  </FormDescription> *!/*/}
            {/*      <FormMessage />*/}
            {/*    </FormItem>*/}
            {/*  )}*/}
            {/*/>*/}
            {/*<FormField*/}
            {/*  control={form.control}*/}
            {/*  name="confirmPassword"*/}
            {/*  render={({ field }) => (*/}
            {/*    <FormItem>*/}
            {/*      <FormLabel>Confirm password</FormLabel>*/}
            {/*      <FormControl>*/}
            {/*        <Input*/}
            {/*          placeholder="Enter password here"*/}
            {/*          {...field}*/}
            {/*          type="password"*/}
            {/*        />*/}
            {/*      </FormControl>*/}
            {/*      /!* <FormDescription>*/}
            {/*    This is your public display name.*/}
            {/*  </FormDescription> *!/*/}
            {/*      <FormMessage />*/}
            {/*    </FormItem>*/}
            {/*  )}*/}
            {/*/>*/}
            <FormField
              control={form.control}
              render={({ field }) => {
                return (
                  <FormItem>
                    <span className="flex items-center justify-between">
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={field.disabled}
                      />
                      <p className="w-10/12 text-sm text-slate-400">
                        I would like to receive marketing and announcement email
                        from Motito.
                      </p>
                    </span>
                  </FormItem>
                );
              }}
              name={'marketing_consent'}
            />

            <Button
              id="motito-sign-up"
              loading={authStore.isRequestingOTP || authStore.isLookingUpPhone}
              disabled={authStore.isRequestingOTP || authStore.isLookingUpPhone}
              type="submit"
              className="w-full"
            >
              {isPartnerPrevalidated ? 'Continue' : 'Send Verification Code'}
            </Button>
          </form>
        </Form>
      </>
    );
  }
);
